import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Input
} from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { fetchClergy } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';


interface ClergyMember {
  id: number;
  name: string;
  title: string;
  slug: string;
  archdeaconry: string;
  parish: string;
  parish_location?: string;
  email?: string;
  phone?: string;
  rank?: string;
  photo?: string;
  hierarchy?: number;
}

const Clergy = () => {
  const [clergy, setClergy] = useState<ClergyMember[]>([]);
  const [filteredClergy, setFilteredClergy] = useState<ClergyMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRank, setFilterRank] = useState('all');
  const [groupBy, setGroupBy] = useState<'archdeaconry' | 'hierarchy'>('archdeaconry');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  useEffect(() => {
    const loadClergy = async () => {
      try {
        const response = await fetchClergy();
        const transformed = response.data.priests.map((item: any) => ({
          id: item.id,
          name: item.name,
          title: item.title,
          slug: item.slug,
          archdeaconry: item.archdeaconry?.name ?? 'Unknown',
          parish: item.parish?.name ?? 'Unknown',
          parish_location: item.parish?.location ?? '',
          email: item.email,
          phone: item.phone,
          rank: item.assignments?.[0]?.role ?? '',
          hierarchy: item.hierarchy ?? 999,
          photo: item.photo ?? 'https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png',
        }));
        setClergy(transformed);
        setFilteredClergy(transformed);
      } catch (error) {
        console.error('Error loading clergy:', error);
        toast({
          title: 'Error',
          description: 'Could not fetch clergy list.',
          variant: 'destructive',
        });
      }
    };

    loadClergy();
  }, [toast]);

  // Filter & sort clergy
  useEffect(() => {
    let filtered = clergy
      .filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.parish.toLowerCase().includes(searchTerm.toLowerCase())
      )

    if (filterRank !== 'all') {
      filtered = filtered.filter(member => member.rank === filterRank);
    }

    if (groupBy === 'hierarchy') {
      filtered.sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999));
    }

    setFilteredClergy(filtered);
  }, [clergy, searchTerm, filterRank, groupBy]);

  const ranks = [...new Set(
    clergy
      .map(member => member.rank)
      .filter(Boolean)
      .filter(r => !['provost', 'sub'].includes(r.toLowerCase()))
  )];

  const groupedByArch = filteredClergy.reduce((acc, member) => {
    if (!acc[member.archdeaconry]) acc[member.archdeaconry] = [];
    acc[member.archdeaconry].push(member);
    return acc;
  }, {} as Record<string, ClergyMember[]>);

  const toggleExpand = (key: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    // Collapse all by default on small screens
    if (window.innerWidth < 768) {
      const collapsed: Record<string, boolean> = {};
      Object.keys(groupedByArch).forEach(k => {
        collapsed[k] = false;
      });
      setExpandedGroups(collapsed);
    } else {
      const expanded: Record<string, boolean> = {};
      Object.keys(groupedByArch).forEach(k => {
        expanded[k] = true;
      });
      setExpandedGroups(expanded);
    }
  }, [filteredClergy]);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-gray-500" />
            <Input
              placeholder="Search name or parish"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full sm:w-80"
            />
          </div>
          <div className="flex gap-4">
            <Select value={groupBy} onValueChange={v => setGroupBy(v as 'archdeaconry' | 'hierarchy')}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Group By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="archdeaconry">By Archdeaconry</SelectItem>
                <SelectItem value="hierarchy">By Hierarchy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {groupBy === 'hierarchy' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredClergy.map(member => (
              <Card key={member.id}>
                <CardHeader>
                  <CardTitle>
                    <Link to={`/clergy/${member.slug}`}>
                      {member.title} {member.name}
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <img src={member.photo} alt={member.name} className="w-full h-48 object-cover rounded-md mb-2" />
                  <p><strong>Parish:</strong> {member.parish}, {member.parish_location}</p>
                  {member.email && <p><strong>Email:</strong> <Link to={`mailto:${member.email}`}>{member.email}</Link></p>}
                  {member.phone && <p><strong>Phone:</strong> <Link to={`tel:${member.phone}`}>{member.phone}</Link></p>}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          Object.entries(groupedByArch).map(([arch, members]) => {
            const isOpen = expandedGroups[arch] ?? false;
            return (
              <div key={arch} className="mb-10">
                <div
                  className="flex items-center justify-between cursor-pointer bg-white p-3 rounded shadow-sm border"
                  onClick={() => toggleExpand(arch)}
                >
                  <h2 className="text-lg font-bold text-gray-700">
                    {arch} ({members.length})
                  </h2>
                  {isOpen ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </div>
                {isOpen && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {members
                      .sort((a, b) => (a.hierarchy ?? 999) - (b.hierarchy ?? 999))
                      .map(member => (
                        <Card key={member.id}>
                          <CardHeader>
                            <CardTitle>
                              <Link to={`/clergy/${member.slug}`}>
                                {member.title} {member.name}
                              </Link>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <img src={member.photo} alt={member.name} className="w-full h-48 object-cover rounded-md mb-2" />
                            <p><strong>Parish:</strong> {member.parish}, {member.parish_location}</p>
                            {member.email && <p><strong>Email:</strong> <Link to={`mailto:${member.email}`}>{member.email}</Link></p>}
                            {member.phone && <p><strong>Phone:</strong> <Link to={`tel:${member.phone}`}>{member.phone}</Link></p>}
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Clergy;
